import Promise from 'bluebird';
import _ from 'lodash';
import numeral from 'numeral';
import templates from '../../../core/templates';
import { customScrollbar } from '../../../shared';
import { isValidNumber } from './VectorLayerUtils';

// import 'mapbox.js';

export class LegendLayer {
    static getTemplate (type, values) {
        if (_.isArray(type)) {
            return templates(`${type[0]}-${type[1]}`, values);
        } else {
            return templates(`map-${type}`, values);
        }
    }

    constructor ({ map, options, colorRange, getColorRange, features }) {
        this.map = map;
        this.options = options;
        this.getColorRange = getColorRange;
        this.colorRange = colorRange;
        this.features = features;

        this.add();
    }

    add () {
        // console.time('L.VectorLayer.LegendLayer -> add');
        try {
            this.create().then((template) => {
                if (this.removed) {
                    return;
                }

                this.layerControl = L.control({ position: 'bottomleft' });
                this.layerControl.onAdd = () => template;
                this.map.addControl(this.layerControl);
            });
        } catch (err) {
            console.log('ERROR: ', err);
        }
        // console.timeEnd('L.VectorLayer.LegendLayer -> add');
    }

    remove () {
        // console.time('L.VectorLayer.LegendLayer -> remove');
        this.removed = true;

        try {
            if (this.layerControl) {
                this.map.removeControl(this.layerControl);
                delete this.layerControl;
            }
        } catch (err) {
            console.log('ERROR: ', err);
        }
        // console.timeEnd('L.VectorLayer.LegendLayer -> remove');
    }

    create () {
        return new Promise((resolve, reject) => {
            // console.time('L.VectorLayer.LegendLayer -> create');

            const templateValues = {
                title: this.options.legend.title,
                subtitle: this.options.legend.subtitle,
                items: []
            };

            const createTemplate = (name) => {
                const legend = $(LegendLayer.getTemplate(name, templateValues));
                resolve(legend[0]);

                legend.find('.legend-info').tooltip({ container: 'body', placement: 'right' });

                if (_.size(templateValues.items) > 12) {
                    setTimeout(() => customScrollbar(legend), 150);
                }

                legend.find('.legend-control').on('click', (e) => {
                    const $icon = $(e.target).closest('.layer-legend').find('.fa');
                    const $items = $(e.target).closest('.layer-legend').find('.legend-items');
                    if ($items.is(':visible')) {
                        $icon.removeClass('fa-minus-square').addClass('fa-plus-square');
                        $items.hide(700);
                    } else {
                        $icon.removeClass('fa-plus-square').addClass('fa-minus-square');
                        $items.show(700);
                    }
                });

                // console.timeEnd('L.VectorLayer.LegendLayer -> create');
            };

            if ((this.options.symbology || {}).type === 'choropleth') {
                const range = this.colorRange;
                if (!range || !_.isFunction(range.domain)) return;
                const domain = range.domain();

                templateValues.items = _(domain)
                    .filter(v => {
                        if (this.options.exclude) {
                            return !_.some(this.options.exclude, e => e === v);
                        }

                        if ((this.options.legend || {}).mask === "" && isValidNumber(v)) {
                            return false;
                        }

                        return true;
                    })
                    .map((v, i) => {
                        const format = (this.options.legend || {}).mask ? this.options.legend.mask : '0,0[.]00';
                        const value = v;

                        let curValue;
                        let nextValue;

                        if (isValidNumber(value)) {
                            const val = Number(value);
                            const nextVal = Number(domain[i + 1]);
                            curValue = numeral(val).format(format);
                            nextValue = numeral(nextVal || 0).format(format);
                        } else {
                            if (v.includes("+")) {
                                curValue = v;
                                nextValue = domain[i + 1];
                            }

                            if (!v.includes("+")) {
                                curValue = _.capitalize(v);
                                nextValue = _.capitalize(domain[i + 1]);
                            }
                        }

                        const isUniq = (this.options.legend || {}).type === 'uniq' || i === domain.length - 1 || Number(domain[i + 1]) < Number(value);
                        const text = isUniq ? `${curValue}` : `${curValue} : ${nextValue}`;

                        if (!isValidNumber(value) && v.includes("+") && !v.includes("A++") && !v.includes("A+")) {
                            return {
                                color: '#BDBDBD',
                                text
                            };
                        }

                        return {
                            color: this.getColorRange(v),
                            text
                        };
                    })
                    .uniqBy('text')
                    .value();

                createTemplate('layer-legend-polygon');
            } else {
                if (this.options.legend.icon && this.options.legend.text) {
                    Promise.map(this.features, f => {
                        let { icon, text } = this.options.legend;

                        const output = { icon, text };

                        if (_.isString(text) || _.isString(icon)) {
                            _.forEach(f.properties, (value, property) => {
                                const re = new RegExp(`{${property}}`, 'g');
                                if (_.isString(icon)) { output.icon = output.icon.replace(re, String(value).toUnderscoreCase()); }
                                if (_.isString(text)) { output.text = output.text.replace(re, value); }
                            });
                        }

                        if (_.isFunction(text)) {
                            output.text = text(f.properties);
                        }

                        if (_.isFunction(this.options.legend.description)) {
                            output.description = this.options.legend.description(f.properties);
                        }

                        if (_.isFunction(this.options.legend.order)) {
                            output.order = this.options.legend.order(f.properties);
                        }

                        if (_.isFunction(icon)) {
                            return Promise.resolve(icon(f.properties)).then(i => {
                                output.icon = i;
                                return output;
                            });
                        } else {
                            return output;
                        }
                    }).then(res => {
                        templateValues.items = _.uniqBy(res, i => `${i.icon}/${i.text}`);
                        if (_.isFunction(this.options.legend.order)) {
                            templateValues.items = _.sortBy(templateValues.items, 'order');
                        }
                        createTemplate('layer-legend-point');
                    });
                } else {
                    createTemplate('layer-legend-point');
                }
            }
        });
    }
}



// WEBPACK FOOTER //
// ./src/scripts/layers/Leaflet/VectorLayer/LegendLayer.js